import axios, { type AxiosError, type AxiosRequestConfig } from 'axios';
import i18n from '@/i18n';
import { getAccessToken, useSessionStore } from '@/stores/session-store';
import type { ApiErrorBody, ApiResponse } from '@/types/api';

export class ApiClientError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly requestId: string,
    readonly error: ApiErrorBody,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

interface AuthRequestConfig extends AxiosRequestConfig {
  _authRetried?: boolean;
  _skipAuthRefresh?: boolean;
  _authToken?: string;
}

const client = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? '/api/v1',
  timeout: 15_000,
  withCredentials: true,
  headers: { Accept: 'application/json' },
});

let refreshPromise: Promise<string> | null = null;

const sessionInvalidatingCodes = new Set(['AUTH_TOKEN_INVALID', 'AUTH_TOKEN_EXPIRED']);

const apiErrorFromFailure = (failure: AxiosError<ApiResponse<never>>) => {
  const body = failure.response?.data;
  return new ApiClientError(
    body?.message || failure.message,
    failure.response?.status ?? 0,
    body?.requestId ?? 'unknown',
    body?.error ?? { code: 'SYS_NETWORK_ERROR', category: 'network', retryable: true },
  );
};

const refreshAccessToken = async () => {
  const session = useSessionStore.getState();
  session.setStatus('refreshing');
  const response = await client.request<ApiResponse<{ accessToken: string; expiresAt: string }>>({
    url: '/session/refresh',
    method: 'POST',
    _skipAuthRefresh: true,
  } as AuthRequestConfig);
  const token = response.data.data?.accessToken;
  if (response.data.code !== response.status || !token) {
    throw new ApiClientError('工作階段更新失敗', response.status, response.data.requestId, {
      code: 'AUTH_TOKEN_INVALID', category: 'authentication', retryable: false,
    });
  }
  session.establishSession(token);
  return token;
};

const getOrCreateRefresh = () => {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
};

client.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    (config as AuthRequestConfig)._authToken = token;
  }
  config.headers['Accept-Language'] = i18n.language || 'zh-TW';
  config.headers['X-Request-ID'] = crypto.randomUUID();
  return config;
});

client.interceptors.response.use(
  (response) => response,
  async (failure: AxiosError<ApiResponse<never>>) => {
    if (axios.isCancel(failure)) return Promise.reject(failure);
    const body = failure.response?.data;
    const config = (failure.config ?? {}) as AuthRequestConfig;
    const url = config.url ?? '';
    const isLoginRequest = url.endsWith('/session/login');
    const isRefreshRequest = url.endsWith('/session/refresh');
    const tokenIsCurrent = !config._authToken || config._authToken === getAccessToken();
    const canRefresh = failure.response?.status === 401
      && body?.error?.code === 'AUTH_TOKEN_EXPIRED'
      && tokenIsCurrent
      && !config._authRetried
      && !config._skipAuthRefresh
      && !isLoginRequest
      && !isRefreshRequest;

    if (canRefresh) {
      try {
        const token = await getOrCreateRefresh();
        config._authRetried = true;
        config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
        return await client.request(config);
      } catch (refreshError) {
        useSessionStore.getState().clearSession('expired');
        return Promise.reject(refreshError);
      }
    }

    const invalidatesSession = failure.response?.status === 401
      && Boolean(body?.error?.code)
      && sessionInvalidatingCodes.has(body!.error!.code);
    if (invalidatesSession && tokenIsCurrent && !isLoginRequest && !config._skipAuthRefresh) {
      useSessionStore.getState().clearSession('expired');
    }
    return Promise.reject(apiErrorFromFailure(failure));
  },
);

export async function apiRequest<T>(config: AxiosRequestConfig): Promise<ApiResponse<T>> {
  const response = await client.request<ApiResponse<T>>(config);
  if (response.data.code !== response.status) {
    throw new ApiClientError('API 回應狀態不一致', response.status, response.data.requestId, {
      code: 'SYS_CONTRACT_MISMATCH',
      category: 'contract',
      retryable: false,
    });
  }
  return response.data;
}

export const apiMutator = async <T>(config: AxiosRequestConfig, options?: AxiosRequestConfig): Promise<T> => {
  const response = await apiRequest<T>({ ...config, ...options });
  return response.data as T;
};

export const apiClientForTests = client;
export const resetAuthRefreshForTests = () => { refreshPromise = null; };
